import { Fragment } from "react";
import { Popover, Transition } from "@headlessui/react";
import { MenuIcon, XIcon } from "@heroicons/react/outline";
import terapia3 from "./../assets/terapia3.jpg";
import logo from "./../assets/logo.png";
import { Link } from "react-scroll";

const navigation = [
  { name: "Sobre mí", to: "sobremi" },
  { name: "Servicios", to: "servicios" },
  { name: "Certificados", to: "certificados" },
  { name: "Testimonios", to: "testimonios" },
  { name: "Contacto", to: "contacto" },
];

export default function Cabecera() {
  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-primary-lighter via-ocean-lighter to-warm-lighter min-h-screen flex flex-col">
      {/* Decorative blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-primary-light opacity-20 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-80 h-80 bg-ocean-light opacity-20 rounded-full translate-x-1/4 translate-y-1/4 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 right-1/4 w-48 h-48 bg-warm-light opacity-25 rounded-full blur-2xl pointer-events-none" />

      {/* Floating shapes */}
      <div className="absolute top-20 left-10 w-6 h-6 bg-primary rounded-full opacity-30 animate-float" />
      <div className="absolute top-40 left-1/4 w-4 h-4 bg-ocean rounded-full opacity-25 animate-float-delay" />
      <div className="absolute bottom-32 left-16 w-8 h-8 bg-warm rounded-full opacity-20 animate-float-slow" />
      <div className="absolute top-16 right-1/3 w-5 h-5 bg-mint rounded-full opacity-30 animate-float" />

      {/* Navbar */}
      <Popover className="relative z-20">
        <div className="px-4 sm:px-6 lg:px-8 pt-6">
          <nav className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-2xl p-2 shadow-soft">
                <img alt="logo-bzt" className="h-14 w-14" src={logo} />
              </div>
              <div className="hidden lg:block">
                <p className="font-heading text-lg font-semibold text-primary leading-tight">Barbara Zamora</p>
                <p className="text-xs text-gray-500 font-body">T.O. & Profesora de Natación</p>
              </div>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1 bg-white/70 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-soft">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.to}
                  smooth={true}
                  duration={800}
                  className="cursor-pointer font-body font-semibold text-sm text-gray-600 hover:text-primary hover:bg-primary-lighter px-4 py-2 rounded-xl transition-all duration-200 select-none"
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Mobile hamburger */}
            <div className="md:hidden">
              <Popover.Button className="bg-white rounded-xl p-2 inline-flex items-center justify-center text-gray-500 hover:text-primary hover:bg-primary-lighter focus:outline-none focus:ring-2 focus:ring-primary transition-colors duration-200 cursor-pointer">
                <span className="sr-only">Abrir menú</span>
                <MenuIcon className="h-6 w-6" aria-hidden="true" />
              </Popover.Button>
            </div>
          </nav>
        </div>

        {/* Mobile menu */}
        <Transition
          as={Fragment}
          enter="duration-200 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="duration-150 ease-in"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <Popover.Panel focus className="absolute z-30 top-4 inset-x-4 p-2 transition transform origin-top-right md:hidden">
            <div className="rounded-3xl shadow-soft-lg bg-white/95 backdrop-blur-sm ring-1 ring-primary ring-opacity-10 overflow-hidden">
              <div className="px-5 pt-5 pb-2 flex items-center justify-between border-b border-primary-lighter">
                <div className="flex items-center gap-3">
                  <img className="h-10 w-10 rounded-xl" src={logo} alt="logo" />
                  <p className="font-heading font-semibold text-primary">Barbara Zamora</p>
                </div>
                <Popover.Button className="bg-primary-lighter rounded-xl p-2 inline-flex items-center justify-center text-primary hover:bg-primary hover:text-white focus:outline-none transition-colors duration-200 cursor-pointer">
                  <span className="sr-only">Cerrar menú</span>
                  <XIcon className="h-5 w-5" aria-hidden="true" />
                </Popover.Button>
              </div>
              <div className="px-3 py-4 space-y-1">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.to}
                    smooth={true}
                    duration={800}
                    className="cursor-pointer block px-4 py-3 rounded-2xl text-base font-semibold font-body text-gray-700 hover:text-primary hover:bg-primary-lighter transition-colors duration-200"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </Popover.Panel>
        </Transition>
      </Popover>

      {/* Hero content */}
      <div className="flex-1 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-12 lg:py-0">
          <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
            {/* Text */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur-sm rounded-2xl px-4 py-2 mb-6 shadow-soft">
                <span className="w-2 h-2 bg-mint rounded-full animate-pulse" />
                <span className="text-sm font-semibold font-body text-gray-600">Disponible para nuevos pacientes</span>
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight text-gray-800 mb-4">
                <span className="block">Barbara Cecilia</span>
                <span className="block">Zamora Tapia</span>
              </h1>

              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 justify-center lg:justify-start mb-6">
                <span className="inline-flex items-center gap-2 bg-primary text-white font-body font-semibold px-5 py-2.5 rounded-2xl shadow-soft text-base">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  Terapeuta Ocupacional
                </span>
                <span className="inline-flex items-center gap-2 bg-ocean text-white font-body font-semibold px-5 py-2.5 rounded-2xl shadow-ocean text-base">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Profesora de Natación
                </span>
              </div>

              <p className="text-gray-600 font-body text-lg leading-relaxed max-w-lg mx-auto lg:mx-0 mb-8">
                Profesional con gran interés en el abordaje de niños y adultos
                jóvenes entregando herramientas y habilidades que les puedan ser
                útiles a lo largo de todo el ciclo vital desde el ámbito
                terapéutico o medio acuático.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="servicios"
                  smooth={true}
                  duration={800}
                  className="cursor-pointer inline-flex items-center justify-center gap-2 bg-warm hover:bg-orange-600 text-white font-body font-bold px-8 py-4 rounded-2xl shadow-soft transition-all duration-200 hover:scale-105 hover:shadow-lg text-base"
                >
                  Ver Servicios
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link
                  to="contacto"
                  smooth={true}
                  duration={800}
                  className="cursor-pointer inline-flex items-center justify-center gap-2 bg-white hover:bg-primary-lighter text-primary font-body font-bold px-8 py-4 rounded-2xl shadow-soft transition-all duration-200 hover:scale-105 border-2 border-primary/20 text-base"
                >
                  Contactar
                </Link>
              </div>

              {/* Stats */}
              <div className="mt-10 flex flex-wrap gap-6 justify-center lg:justify-start">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-primary rounded-2xl flex items-center justify-center shadow-soft">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-heading text-xl font-semibold text-gray-800">0–18 años</p>
                    <p className="text-xs text-gray-500 font-body">Terapia Ocupacional</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-ocean rounded-2xl flex items-center justify-center shadow-ocean">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-heading text-xl font-semibold text-gray-800">+7 años</p>
                    <p className="text-xs text-gray-500 font-body">Experiencia en natación</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-warm rounded-2xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-heading text-xl font-semibold text-gray-800">12 cursos</p>
                    <p className="text-xs text-gray-500 font-body">Certificados</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Image */}
            <div className="mt-12 lg:mt-0 relative flex justify-center">
              <div className="relative">
                {/* Background shape */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-ocean rounded-4xl opacity-20 blur-2xl transform scale-110" />
                <div className="relative rounded-4xl overflow-hidden shadow-soft-lg border-4 border-white/80 w-full max-w-lg">
                  <img
                    src={terapia3}
                    alt="Barbara Zamora Tapia - Terapeuta Ocupacional"
                    className="w-full h-96 sm:h-[480px] lg:h-[640px] object-cover object-center"
                  />
                  {/* Overlay badge */}
                  <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-2xl px-4 py-3 shadow-soft">
                    <p className="font-heading text-base font-semibold text-gray-800">Universidad de los Andes (2019)</p>
                    <p className="text-xs text-primary font-body">Terapeuta Ocupacional con mención en Pediatría</p>
                  </div>
                </div>

                {/* Floating cards */}
                <div className="absolute -left-6 top-12 bg-white rounded-2xl p-3 shadow-soft-lg flex items-center gap-2 animate-float">
                  <div className="w-8 h-8 bg-primary-lighter rounded-xl flex items-center justify-center">
                    <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold font-heading text-gray-800">Inclusión</p>
                    <p className="text-xs text-gray-400 font-body">Educativa</p>
                  </div>
                </div>

                <div className="absolute -right-6 top-1/3 bg-white rounded-2xl p-3 shadow-soft-lg flex items-center gap-2 animate-float-delay">
                  <div className="w-8 h-8 bg-ocean-lighter rounded-xl flex items-center justify-center">
                    <svg className="w-4 h-4 text-ocean" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-xs font-bold font-heading text-gray-800">SwimAmerica</p>
                    <p className="text-xs text-gray-400 font-body">Certificada</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave bottom */}
      <div className="w-full overflow-hidden leading-none mt-8">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#ffffff" />
        </svg>
      </div>
    </div>
  );
}
